import { Lang } from '../i18n/locales';

export const brandContent: Record<Lang, any> = {
  en: {
    common: {
      tbc: 'To Be Confirmed',
      loading: 'Loading...',
      untitled: 'Untitled',
      back: 'Back',
      prev: 'Previous',
      next: 'Next',
      contentUnavailable: 'Content Unavailable',
      contentUnavailableDesc: 'This content is currently unavailable.',
      redirectingIn: 'Redirecting in:',
      goHomeNow: 'Go Home',
    },
    auth: {
      login: 'Login',
      logout: 'Logout',
      account: 'Account',
      email: 'Email',
      password: 'Password',
      signup: 'Sign Up',
      signin: 'Sign In',
    },
    cart: {
      title: 'Cart',
      empty: 'Your cart is empty',
      checkout: 'Checkout',
      subtotal: 'Subtotal',
      viewCart: 'View Cart',
      clear: 'Clear',
    },
    products: {
      title: 'Products',
      details: 'Details',
      add: 'Add to Cart',
      outOfStock: 'Out of Stock',
      featured: 'Featured',
    },
    journal: {
      title: 'Journal',
      read: 'Read More',
      tags: 'Tags',
      categories: 'Categories',
      minRead: 'min read',
      pairsWith: 'Pairs with',
    },
    meta: {
      title: 'Natuvía | Natural Path, Made Simple',
      description:
        'Nature-first, single-animal-protein focused nutrition with minimal ingredients and clear labeling.',
    },
    nav: {
      about: 'About',
      products: 'Products',
      journal: 'Journal',
      faq: 'FAQ',
      petServices: 'Pet Services',
      contact: 'Contact',
      styleGuide: 'Style Guide',
      shop: 'Shop Now',
    },
    footer: {
      brandDesc:
        'Natural Path, Made Simple.\nWe make pet nutrition clearer: fewer ingredients, clearer sourcing, easier decisions.',
      explore: 'Explore',
      connect: 'Connect',
      copyright: 'Natuvía (Natuvia). All rights reserved.',
    },
    home: {
      hero: {
        tagline: 'Know what you feed.',
        title: 'Pure ingredients, artisan-crafted.',
        subtitle:
          'Nature-first, made with restraint—clearly labeled ingredients and primary protein, so everyday care feels easier.',
        ctaMain: 'Explore products',
        ctaSec: 'Learn more about us',
      },
      values: [
        { title: 'Pure ingredients', desc: 'Simple, recognizable, no fluff.' },
        { title: 'Artisan-crafted', desc: 'Thoughtfully made, refined in the details.' },
        {
          title: 'Clearly labeled',
          desc: 'Ingredients, primary protein, and how to feed—easy to read.',
        },
      ],
      about: {
        line1:
          'Natuvía (Natuvia) stands for Natural + Via—your pet’s clearer path to nature-first nutrition.',
        line2:
          'Single-protein focused, minimal ingredients, and clear labeling—so you always know what you’re feeding.',
        cta: 'Meet Natuvía',
      },
      productCTA: {
        lead: 'Start with the primary protein—then scan ingredients and feeding guidance. No guessing.',
        btnA: 'View ingredients & protein',
        btnB: 'Shop now',
      },
      journalCTA: {
        copy: 'Want a clearer way to read labels and choose the right protein? Explore our Journal—plain-language guides for everyday feeding.',
        cta: 'Read the Journal',
      },
      newsletter: {
        copy: 'One email a week: feeding tips, ingredient explainers, and protein guides—short and helpful.',
        cta: 'Subscribe',
      },
    },
    aboutPage: {
      h1: 'About Us',
      seo: {
        title: 'About Us | Natuvia — Natural Ingredients, Artisan-Crafted',
        description:
          'Natuvia (Natural + Via) is a clearer natural path for everyday feeding—recognizable ingredients, purposeful formulas, and verifiable information, so you can feed with confidence.',
      },
      hero: {
        tagline: 'Made to be trusted.',
        paragraph:
          "Natuvía comes from Natural + Via—the natural way. We believe caring for your pet shouldn't be guesswork. Recognizable ingredients, purposeful formulas, verifiable information—feeding with confidence becomes easier, and easier to sustain.",
      },
      founderStory: {
        title: 'The Story of "Chor Yat"',
        subtitle: 'How a rescued kitten inspired Natuvía',
        paragraphs: [
          'Natuvía began with a cat that was almost mistaken for dead.',
          "It was the first day of Chinese New Year. I spotted a motionless kitten on the roadside and couldn't bear to leave it there. When I picked it up to move it aside, I realized it was still breathing.",
          'The kitten was barely two weeks old, suffering from pneumonia, dehydration, cat flu, anemia, and malnutrition—covered in fleas. The vet said recovery was unlikely.',
          'For the next week, I fed that kitten every few hours. I never considered myself a cat person, but by the end of that week, I had fallen in love. I had planned to find it a new home once recovered, but ended up keeping it myself. I named it "Chor Yat"—meaning "the first day."',
          'Because Chor Yat was always fragile, I started seriously researching cat nutrition. But I found the market overwhelming—most options looked the same, ingredient labels were confusing, and it was nearly impossible to know what I was actually feeding.',
          'Three years later, Chor Yat passed away from cancer triggered by FIV and feline leukemia contracted as a kitten.',
          'After losing Chor Yat, one question stayed with me: What if there had been a better choice back then?',
        ],
        closing:
          "That question became Natuvía's starting point—I wanted to create pet food that owners could truly understand and trust.",
      },
      philosophy: {
        title: 'Our Philosophy: Less, But Better—Built for the Long Term',
        body: 'We build our formulas around a single animal protein, keeping ingredients simple. Every ingredient has a clear purpose, explained in plain language.\n\nNo buzzwords. No inflated claims. Just honest, verifiable information and choices.',
      },
      craft: {
        title: 'Craft Lives in the Details',
        body: "True craft doesn't need fanfare—it shows in every step: from sourcing ingredients and refining formulas to presenting information. One standard guides everything we do—Natural (ingredients) | Thoughtful (craft) | Clear (labeling).",
      },
      productInfo: {
        title: "What You'll Find in Every Product",
        bullets: [
          'Primary Protein Source — Clearly identified animal protein at the core',
          'Complete Ingredient List — Every ingredient and formula component laid out',
          'Feeding Guidelines — Practical, easy to follow, adjustable to your needs',
        ],
        closing: 'Clarity is our commitment.',
      },
      cta: {
        title: 'Start with the primary protein—choose with clarity',
        copy: 'Check the primary protein first, then scan the full ingredient list and feeding guidance—so every choice feels grounded and confident.',
        primary: 'Explore products',
        secondary: 'Read the Journal',
      },
    },
    shippingPage: {
      h1: 'Shipping & Returns',
      seo: {
        title: 'Shipping & Returns | Natuvia',
        description:
          'Learn about Natuvia shipping options, delivery times, and our hassle-free return policy.',
      },
      hero: {
        tagline: 'Shipping & Returns',
        paragraph: 'Everything you need to know about getting your order and our return policy.',
      },
      shipping: {
        title: 'Shipping Information',
        items: [
          {
            title: 'Hong Kong Delivery',
            desc: 'Free shipping on orders over HK$300. Standard delivery takes 2-4 business days.',
          },
          {
            title: 'International Shipping',
            desc: 'We currently ship to selected regions. Shipping fees and delivery times vary by location.',
          },
          {
            title: 'Order Processing',
            desc: "Orders are processed within 1-2 business days. You'll receive a tracking number once shipped.",
          },
        ],
      },
      returns: {
        title: 'Returns & Refunds',
        items: [
          {
            title: '14-Day Return Policy',
            desc: 'Not satisfied? Return unopened products within 14 days for a full refund.',
          },
          {
            title: 'Damaged Products',
            desc: "Received a damaged item? Contact us within 48 hours with photos and we'll make it right.",
          },
          {
            title: 'How to Return',
            desc: 'Email us at info@natuviapet.com with your order number to initiate a return.',
          },
        ],
      },
      cta: {
        title: 'Have questions?',
        copy: 'Our team is here to help with any shipping or return inquiries.',
        button: 'Contact Us',
      },
    },
    privacyPage: {
      h1: 'Privacy Policy',
      seo: {
        title: 'Privacy Policy | Natuvia',
        description: 'Learn how Natuvia collects, uses, and protects your personal information.',
      },
      hero: {
        tagline: 'Privacy Policy',
        paragraph:
          'Your privacy matters to us. This policy explains how we handle your information.',
      },
      lastUpdated: 'Last updated: January 2025',
      sections: [
        {
          title: '1. Information We Collect',
          content: `We collect information you provide directly to us, including:

• **Account Information**: When you create an account, we collect your email address and password.
• **Order Information**: When you make a purchase, we collect your name, shipping address, billing address, phone number, and payment information.
• **Pet Information**: If you choose to create pet profiles, we collect pet names, types, birthdays, photos, and food preferences.
• **Communications**: When you contact us, we collect your name, email, and message content.
• **Usage Data**: We automatically collect information about how you use our website, including pages visited, time spent, and device information.`,
        },
        {
          title: '2. How We Use Your Information',
          content: `We use the information we collect to:

• Process and fulfill your orders
• Send order confirmations and shipping updates
• Provide customer support
• Personalize your shopping experience
• Improve our products and services
• Send promotional communications (with your consent)
• Comply with legal obligations`,
        },
        {
          title: '3. Information Sharing',
          content: `We do not sell your personal information. We may share your information with:

• **Service Providers**: Third parties that help us operate our business (payment processors, shipping carriers, analytics providers)
• **Legal Requirements**: When required by law or to protect our rights
• **Business Transfers**: In connection with a merger, acquisition, or sale of assets`,
        },
        {
          title: '4. Data Security',
          content: `We implement appropriate technical and organizational measures to protect your personal information, including:

• Encryption of sensitive data in transit and at rest
• Secure payment processing through Stripe
• Regular security assessments
• Access controls limiting who can access your data`,
        },
        {
          title: '5. Your Rights',
          content: `You have the right to:

• Access the personal information we hold about you
• Correct inaccurate information
• Delete your account and associated data
• Opt out of marketing communications
• Export your data in a portable format

To exercise these rights, please contact us at info@natuviapet.com.`,
        },
        {
          title: '6. Cookies and Tracking',
          content: `We use cookies and similar technologies to:

• Remember your preferences and login status
• Analyze website traffic and usage patterns
• Improve website performance

You can control cookies through your browser settings. Note that disabling cookies may affect website functionality.`,
        },
        {
          title: '7. Third-Party Services',
          content: `Our website integrates with third-party services including:

• **Supabase**: For authentication and data storage
• **Stripe**: For payment processing
• **Plausible Analytics**: For privacy-friendly website analytics
• **Google Fonts**: For typography

Each service has its own privacy policy governing their use of your data.`,
        },
        {
          title: '8. AI Chatbot & Conversation Logs',
          content: `Our website includes an AI chatbot that helps you with pet nutrition questions. To improve service quality and to refine our AI models, we log chatbot conversations:

• **What we store**: Each user message and the assistant's reply, plus a session identifier, language, and approximate country (from Cloudflare).
• **What we do not store from chats**: We never store payment details, passwords, or government IDs. Please do not share these in chat.
• **Retention**: Conversation logs are automatically deleted after 90 days.
• **Use**: We use the logs to improve answer quality, debug issues, and (with care) to fine-tune or evaluate AI models. We do not sell chat content.
• **Your rights**: You can request export or deletion of your chat history at any time at info@natuviapet.com.

A short reminder is shown beneath the chat input. The AI's answers are not medical advice — please consult a veterinarian for diagnosis and treatment.`,
        },
        {
          title: '9. Data Retention',
          content: `We retain your personal information for as long as necessary to:

• Provide our services to you
• Comply with legal obligations
• Resolve disputes
• Enforce our agreements

Chatbot conversation logs are auto-deleted after 90 days. You may request deletion of your account at any time.`,
        },
        {
          title: "10. Children's Privacy",
          content: `Our services are not directed to children under 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child, please contact us immediately.`,
        },
        {
          title: '11. Changes to This Policy',
          content: `We may update this privacy policy from time to time. We will notify you of significant changes by email or through our website. Your continued use of our services after changes become effective constitutes acceptance of the updated policy.`,
        },
        {
          title: '12. Contact Us',
          content: `If you have questions about this privacy policy or our data practices, please contact us:

**Email**: info@natuviapet.com
**Instagram**: @NatuviaPet

We typically respond within 24-48 hours.`,
        },
      ],
    },
    termsPage: {
      h1: 'Terms & Conditions',
      seo: {
        title: 'Terms & Conditions | Natuvia',
        description:
          'Read the terms and conditions for using Natuvia website and purchasing our products.',
      },
      hero: {
        tagline: 'Terms & Conditions',
        paragraph:
          'Please read these terms carefully before using our website or making a purchase.',
      },
      lastUpdated: 'Last updated: January 2025',
      sections: [
        {
          title: '1. Acceptance of Terms',
          content: `By accessing or using the Natuvia website (natuvia.com), you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our website or services.

These terms apply to all visitors, users, and customers of our website.`,
        },
        {
          title: '2. Products and Services',
          content: `**Product Information**
We strive to provide accurate product descriptions, images, and pricing. However, we do not warrant that product descriptions or other content is accurate, complete, or error-free.

**Availability**
All products are subject to availability. We reserve the right to limit quantities and discontinue products without notice.

**Pricing**
Prices are displayed in the selected currency and are subject to change. Any promotions or discounts are valid for the specified period only.`,
        },
        {
          title: '3. Orders and Payment',
          content: `**Order Acceptance**
Your order constitutes an offer to purchase. We reserve the right to accept or decline any order for any reason, including product availability, errors in pricing, or suspected fraud.

**Payment**
We accept major credit cards and other payment methods as displayed at checkout. Payment is processed securely through Stripe. You agree to provide accurate payment information.

**Order Confirmation**
You will receive an email confirmation once your order is placed. This confirmation does not guarantee acceptance of your order.`,
        },
        {
          title: '4. Shipping and Delivery',
          content: `**Shipping Areas**
We currently ship to Hong Kong and selected international regions. Shipping availability and costs are displayed at checkout.

**Delivery Times**
Estimated delivery times are provided for reference only and are not guaranteed. Natuvia is not responsible for delays caused by shipping carriers or circumstances beyond our control.

**Risk of Loss**
Risk of loss and title for products pass to you upon delivery to the shipping carrier.`,
        },
        {
          title: '5. Returns and Refunds',
          content: `**Return Policy**
Unopened products may be returned within 14 days of delivery for a full refund. Products must be in original, unopened condition.

**Damaged or Defective Products**
If you receive a damaged or defective product, contact us within 48 hours of delivery with photos. We will arrange a replacement or refund.

**Refund Processing**
Refunds will be processed to the original payment method within 5-10 business days after we receive the returned product.

**Non-Returnable Items**
Opened products, perishable items, and products past their use-by date cannot be returned.`,
        },
        {
          title: '6. User Accounts',
          content: `**Account Creation**
You may create an account to access additional features. You are responsible for maintaining the confidentiality of your account credentials.

**Account Accuracy**
You agree to provide accurate and complete information when creating an account and to update your information as needed.

**Account Termination**
We reserve the right to suspend or terminate accounts that violate these terms or for any other reason at our discretion.`,
        },
        {
          title: '7. Intellectual Property',
          content: `All content on this website, including text, images, logos, graphics, and software, is the property of Natuvia or its licensors and is protected by intellectual property laws.

You may not reproduce, distribute, modify, or create derivative works from any content without our express written permission.`,
        },
        {
          title: '8. User Content',
          content: `**Pet Profiles and Reviews**
When you submit content such as pet profiles or reviews, you grant Natuvia a non-exclusive, royalty-free license to use, display, and distribute that content.

**Content Standards**
You agree not to submit content that is:
• Illegal, harmful, or offensive
• Infringing on intellectual property rights
• False, misleading, or deceptive
• Spam or unsolicited advertising`,
        },
        {
          title: '9. Disclaimer of Warranties',
          content: `Our website and products are provided "as is" without warranties of any kind, either express or implied.

**Nutritional Information**
Product information, including nutritional content and feeding guidelines, is provided for general reference. Always consult with a veterinarian for specific dietary needs.

**Pet Health**
Natuvia products are not intended to diagnose, treat, cure, or prevent any disease. Consult a veterinarian for health concerns.`,
        },
        {
          title: '10. Limitation of Liability',
          content: `To the maximum extent permitted by law, Natuvia shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from:

• Use or inability to use our website or products
• Any products purchased through our website
• Unauthorized access to your data
• Any third-party content or conduct`,
        },
        {
          title: '11. Indemnification',
          content: `You agree to indemnify and hold Natuvia harmless from any claims, damages, losses, or expenses arising from your use of our website, violation of these terms, or infringement of any third-party rights.`,
        },
        {
          title: '12. Governing Law',
          content: `These terms shall be governed by and construed in accordance with the laws of Hong Kong Special Administrative Region. Any disputes shall be subject to the exclusive jurisdiction of the courts of Hong Kong.`,
        },
        {
          title: '13. Changes to Terms',
          content: `We may update these terms from time to time. Changes will be posted on this page with an updated "Last updated" date. Your continued use of our website after changes constitutes acceptance of the modified terms.`,
        },
        {
          title: '14. Contact Information',
          content: `For questions about these terms, please contact us:

**Email**: info@natuviapet.com
**Instagram**: @NatuviaPet

We typically respond within 24-48 hours.`,
        },
      ],
    },
    faqPage: {
      h1: 'FAQ',
      seo: {
        title: 'Frequently Asked Questions | Natuvia',
        description: 'Find answers to common questions about Natuvia products, shipping, and more.',
      },
      hero: {
        tagline: 'Frequently Asked Questions',
        paragraph: "Can't find what you're looking for? Feel free to contact us.",
      },
      categories: {
        products: 'Products',
        shipping: 'Shipping & Orders',
        feeding: 'Feeding Guide',
        other: 'Other',
      },
      items: [
        {
          category: 'products',
          q: 'What makes Natuvia different from other pet food brands?',
          a: 'We focus on clarity: single animal protein, minimal ingredients, and clear labeling. Every ingredient has a purpose, and we explain it in plain language—no marketing buzzwords.',
        },
        {
          category: 'products',
          q: 'Are your products suitable for pets with allergies?',
          a: 'Our single-protein formulas are designed to make it easier to identify and avoid allergens. Always check the ingredient list and consult your vet if your pet has known allergies.',
        },
        {
          category: 'products',
          q: 'How should I store Natuvia products?',
          a: 'Store in a cool, dry place away from direct sunlight. Once opened, seal tightly and use within the timeframe indicated on the packaging.',
        },
        {
          category: 'feeding',
          q: 'How much should I feed my pet?',
          a: 'Feeding amounts vary by product, pet size, and activity level. Check the feeding guide on each product page for specific recommendations.',
        },
        {
          category: 'feeding',
          q: 'Can I mix Natuvia with other foods?',
          a: "Yes! Our freeze-dried products work great as toppers or treats alongside your pet's regular meals. Adjust portions accordingly.",
        },
        {
          category: 'shipping',
          q: 'How long does delivery take?',
          a: 'Hong Kong orders typically arrive within 2-4 business days. International shipping times vary by location.',
        },
        {
          category: 'shipping',
          q: 'Do you offer free shipping?',
          a: 'Yes! We offer free shipping on Hong Kong orders over HK$300.',
        },
        {
          category: 'shipping',
          q: 'What is your return policy?',
          a: 'We accept returns of unopened products within 14 days. Contact us at info@natuviapet.com to initiate a return.',
        },
        {
          category: 'other',
          q: 'How can I contact Natuvia?',
          a: 'Email us at info@natuviapet.com or reach out via Instagram @NatuviaPet. We typically respond within 24-48 hours.',
        },
        {
          category: 'other',
          q: 'Do you have a physical store?',
          a: "We're currently online-only, which allows us to focus on product quality and keep prices fair.",
        },
      ],
      cta: {
        title: 'Still have questions?',
        copy: "We're happy to help.",
        button: 'Contact Us',
      },
    },
    contactPage: {
      h1: 'Contact Us',
      seo: {
        title: 'Contact Us | Natuvia — Get in Touch',
        description:
          "Have questions about Natuvia products or need assistance? We'd love to hear from you. Reach out via email or social media.",
      },
      hero: {
        tagline: "We'd love to hear from you",
        paragraph:
          "Whether you have questions about our products, need feeding guidance, or just want to say hello — we're here to help.",
      },
      form: {
        title: 'Send us a message',
        name: 'Your Name',
        namePlaceholder: 'Enter your name',
        email: 'Email Address',
        emailPlaceholder: 'your@email.com',
        subject: 'Subject',
        subjectPlaceholder: 'What is this about?',
        message: 'Message',
        messagePlaceholder: 'Tell us how we can help...',
        submit: 'Send Message',
        sending: 'Sending...',
        success: "Message sent! We'll get back to you soon.",
        error: 'Something went wrong. Please try again or email us directly.',
      },
      info: {
        title: 'Other ways to reach us',
        email: {
          label: 'Email',
          value: 'info@natuviapet.com',
        },
        social: {
          label: 'Follow us',
        },
        response: {
          label: 'Response Time',
          value: 'We typically respond within 24-48 hours.',
        },
      },
    },
    chat: {
      title: 'Natuvía Assistant',
      placeholder: 'Ask me anything...',
      welcome: "Hi! I'm your AI assistant. Ask me about our products or nutrition.",
      quickQuestions: {
        title: 'Quick Questions',
        items: [
          'How do I choose the right protein for my pet?',
          'How do I read ingredient labels without getting misled?',
          'Can freeze-dried chicken breast be fed daily? How should I serve it?',
          'For sensitive stomach/itchy skin, how should I think about protein choice?',
          'Treat or topper—how do I pair it with main meals?',
          'Show me the most relevant Journal articles.',
        ],
      },
    },
  },
  'zh-hk': {
    common: {
      tbc: '待定',
      loading: '載入中...',
      untitled: '無標題',
      back: '返回',
      prev: '上一篇',
      next: '下一篇',
      contentUnavailable: '內容暫未提供',
      contentUnavailableDesc: '此內容目前無法顯示。',
      redirectingIn: '即將跳轉：',
      goHomeNow: '返回首頁',
    },
    auth: {
      login: '登入',
      logout: '登出',
      account: '會員中心',
      email: '電子信箱',
      password: '密碼',
      signup: '註冊',
      signin: '登入',
    },
    cart: {
      title: '購物車',
      empty: '購物車是空的',
      checkout: '結帳',
      subtotal: '小計',
      viewCart: '查看購物車',
      clear: '清空',
    },
    products: {
      title: '產品系列',
      details: '查看詳情',
      add: '加入購物車',
      outOfStock: '暫無存貨',
      featured: '精選',
    },
    journal: {
      title: '日誌',
      read: '閱讀全文',
      tags: '標籤',
      categories: '分類',
      minRead: '分鐘閱讀',
      pairsWith: '建議搭配',
    },
    meta: {
      title: 'Natuvía | 自然之道，化繁為簡',
      description: '自然優先、以單一動物性蛋白為主軸，極簡成分並清楚標示，讓你更安心做選擇。',
    },
    nav: {
      about: '關於我們',
      products: '產品系列',
      journal: '日誌',
      faq: '常見問題',
      petServices: '寵物服務',
      contact: '聯絡我們',
      styleGuide: '設計規範',
      shop: '立即購買',
    },
    footer: {
      brandDesc:
        '自然之道，化繁為簡。\n我們讓寵物營養更清晰：更少的成分、更透明的來源、更簡單的選擇。',
      explore: '探索',
      connect: '連結',
      copyright: 'Natuvía (Natuvia). 版權所有。',
    },
    home: {
      hero: {
        tagline: '餵得清楚，養得安心。',
        title: '天然食材・匠心製作',
        subtitle: '以天然、可辨識的食材做起點；成分與主要蛋白清楚標示，令你更安心做長期照顧',
        ctaMain: '探索產品',
        ctaSec: '了解我們',
      },
      values: [
        { title: '天然食材', desc: '嚴選、簡單、看得明' },
        { title: '匠心製作', desc: '細節打磨，質感有底氣' },
        { title: '清楚標示', desc: '成分／主要蛋白／用法一眼就知' },
      ],
      about: {
        line1: 'Natuvía（Natuvia）代表「Natural + Via」——走一條更自然、更清晰的營養之路',
        line2: '我們相信少而精：以單一動物蛋白為主軸、盡量不加不必要添加，並把關鍵資訊講清楚',
        cta: '認識 Natuvía',
      },
      productCTA: {
        lead: '由「主要蛋白」開始選：再看成分表、再看餵食建議，不用估',
        btnA: '查看成分與蛋白來源',
        btnB: '立即選購',
      },
      journalCTA: {
        copy: '想學會如何看成分標籤、怎樣為你的毛孩挑選合適蛋白？前往 Journal 看更多以日常用語撰寫的營養小指南',
        cta: '前往 Journal',
      },
      newsletter: {
        copy: '每週一封：餵食小貼士、成分拆解、蛋白選擇指南（簡短、不煩擾）',
        cta: '訂閱更新',
      },
    },
    aboutPage: {
      h1: '關於我們',
      seo: {
        title: '關於我們｜Natuvia：天然食材，匠心製作',
        description:
          'Natuvia 源自 Natural + Via（自然之路）。以天然可辨識食材、簡潔有目的配方與清楚可查證標示，讓你更安心作出長期餵食選擇',
      },
      hero: {
        tagline: '值得信的選擇',
        paragraph:
          'Natuvía（Natuvia）源自 Natural + Via，意指「自然之路」。我們相信，照顧寵物不必靠猜測——只要食材天然可辨識、配方簡潔而有目的、資訊清楚可查證，日常餵食便能更安心，也更容易長期堅持',
      },
      founderStory: {
        title: '「初一」的故事',
        subtitle: '一隻被救回的幼貓，如何啟發了 Natuvía',
        paragraphs: [
          'Natuvía 的開始，源自一隻差點被當成屍體的貓。',
          '那是農曆大年初一，我在路邊看到一隻一動不動的貓 BB，不忍心讓牠死在路邊，想把牠移走。但當我拿起牠時，發現牠還有氣息。',
          '那是一隻出生大約兩週的幼貓，身上同時有肺炎、脫水、貓感冒、貧血、營養不良，還滿身跳蚤。醫生說，很難救回來。',
          '接下來一星期，我每隔幾小時餵食一次。本來不喜歡貓的我，在那一週愛上了牠。本來打算康復後送養，最後自己留下了。我叫牠「初一」。',
          '因為初一體弱多病，我開始認真研究貓的飲食。但我發現市面上的選擇來來去去都差不多，成分標示也不清不楚，很難知道自己到底在餵什麼。',
          '三年後，初一因為幼貓時期感染的貓愛滋和白血病，引發癌症離開了。',
          '牠走了之後，我一直在想：如果當初有更好的選擇，會不會不一樣？',
        ],
        closing: '這就是 Natuvía 的起點——我想做一個讓主人真正看得懂、放心餵的寵物食品。',
      },
      philosophy: {
        title: '我們的理念：少而精，才適合長期',
        body: '我們以單一動物蛋白為主軸構思配方，盡量保持成分簡單；每項成分皆有明確目的，並以易於理解的方式說明。\n\n我們不堆砌概念字眼，不作誇大承諾，只提供誠實、可查證的資訊與選擇',
      },
      craft: {
        title: '匠心，落在細節',
        body: '「匠心」不必喧嘩，而是體現在每一個環節：從原料挑選、配方取捨，到產品資訊的呈現方式，我們都以同一套標準打磨——自然（食材）｜匠心（製作）｜清楚（標示）',
      },
      productInfo: {
        title: '你會在每款產品看到',
        bullets: [
          '主要蛋白來源：清楚標示配方以哪一種動物蛋白為主',
          '完整成分表：清晰列出所用食材與配方內容',
          '餵食建議：實用、易於跟隨，並可按需要調整',
        ],
        closing: '清楚，是我們的承諾。',
      },
      cta: {
        title: '由主要蛋白開始，作出更清晰的選擇',
        copy: '先看主要蛋白來源，再看完整成分表與餵食建議——讓每一次餵食更踏實、更安心',
        primary: '探索產品',
        secondary: '閱讀 Journal',
      },
    },
    shippingPage: {
      h1: '運送與退換',
      seo: {
        title: '運送與退換｜Natuvia',
        description: '了解 Natuvia 的運送選項、送達時間及退換貨政策。',
      },
      hero: {
        tagline: '運送與退換',
        paragraph: '關於訂單配送和退換貨政策的所有資訊。',
      },
      shipping: {
        title: '運送資訊',
        items: [
          {
            title: '香港本地配送',
            desc: '滿 HK$300 免運費。標準配送需時 2-4 個工作天。',
          },
          {
            title: '海外配送',
            desc: '目前支援部分地區配送，運費及時間因地區而異。',
          },
          {
            title: '訂單處理',
            desc: '訂單會在 1-2 個工作天內處理，出貨後會收到追蹤編號。',
          },
        ],
      },
      returns: {
        title: '退換貨政策',
        items: [
          {
            title: '14 天退貨',
            desc: '不滿意？未開封產品可於 14 天內退回，獲全額退款。',
          },
          {
            title: '產品損壞',
            desc: '收到損壞產品？請在 48 小時內連同照片聯絡我們處理。',
          },
          {
            title: '退貨流程',
            desc: '請發電郵至 info@natuviapet.com，附上訂單編號申請退貨。',
          },
        ],
      },
      cta: {
        title: '有其他問題？',
        copy: '我們樂意協助解答任何運送或退換貨查詢。',
        button: '聯絡我們',
      },
    },
    privacyPage: {
      h1: '私隱政策',
      seo: {
        title: '私隱政策｜Natuvia',
        description: '了解 Natuvia 如何收集、使用及保護你的個人資料。',
      },
      hero: {
        tagline: '私隱政策',
        paragraph: '我們重視你的私隱。本政策說明我們如何處理你的資料。',
      },
      lastUpdated: '最後更新：2025 年 1 月',
      sections: [
        {
          title: '1. 我們收集的資料',
          content: `我們收集你直接提供的資料，包括：

• **帳戶資料**：當你建立帳戶時，我們會收集你的電郵地址和密碼。
• **訂單資料**：當你購物時，我們會收集你的姓名、送貨地址、帳單地址、電話號碼和付款資料。
• **寵物資料**：如你選擇建立寵物檔案，我們會收集寵物名稱、類型、生日、相片和食物偏好。
• **通訊內容**：當你聯絡我們時，我們會收集你的姓名、電郵和訊息內容。
• **使用數據**：我們會自動收集你使用網站的資料，包括瀏覽的頁面、停留時間和裝置資料。`,
        },
        {
          title: '2. 我們如何使用你的資料',
          content: `我們使用收集的資料來：

• 處理和完成你的訂單
• 發送訂單確認和運送更新
• 提供客戶支援
• 個人化你的購物體驗
• 改善我們的產品和服務
• 發送推廣通訊（經你同意）
• 遵守法律義務`,
        },
        {
          title: '3. 資料分享',
          content: `我們不會出售你的個人資料。我們可能會與以下各方分享你的資料：

• **服務供應商**：協助我們營運業務的第三方（付款處理商、運輸公司、分析供應商）
• **法律要求**：當法律要求或為保護我們的權利時
• **業務轉讓**：與合併、收購或資產出售相關時`,
        },
        {
          title: '4. 資料安全',
          content: `我們採取適當的技術和組織措施來保護你的個人資料，包括：

• 傳輸和儲存時加密敏感資料
• 通過 Stripe 進行安全付款處理
• 定期進行安全評估
• 存取控制限制可存取你資料的人員`,
        },
        {
          title: '5. 你的權利',
          content: `你有權：

• 存取我們持有的關於你的個人資料
• 更正不準確的資料
• 刪除你的帳戶和相關資料
• 選擇退出市場推廣通訊
• 以可攜帶格式匯出你的資料

如需行使這些權利，請聯絡 info@natuviapet.com。`,
        },
        {
          title: '6. Cookies 和追蹤',
          content: `我們使用 cookies 和類似技術來：

• 記住你的偏好和登入狀態
• 分析網站流量和使用模式
• 改善網站效能

你可以透過瀏覽器設定控制 cookies。請注意，停用 cookies 可能會影響網站功能。`,
        },
        {
          title: '7. 第三方服務',
          content: `我們的網站整合以下第三方服務：

• **Supabase**：用於認證和資料儲存
• **Stripe**：用於付款處理
• **Plausible Analytics**：用於私隱友善的網站分析
• **Google Fonts**：用於字型

每項服務都有自己的私隱政策規管其對你資料的使用。`,
        },
        {
          title: '8. AI 聊天機械人與對話記錄',
          content: `本網站設有 AI 聊天機械人，協助你解答寵物營養相關問題。為改善服務質素及優化我們的 AI 模型，我們會記錄聊天對話：

• **儲存內容**：每則用戶訊息及助手回覆，連同 session ID、語言及概略所在國家（由 Cloudflare 提供）。
• **不會儲存**：絕不儲存付款資料、密碼或身份證明文件。請勿在對話中提供此類資料。
• **保留期**：對話記錄將於 90 日後自動刪除。
• **用途**：用作改善答案質素、排查問題，以及（謹慎地）用於微調或評估 AI 模型。我們不會出售對話內容。
• **你的權利**：你可以隨時電郵 info@natuviapet.com，要求匯出或刪除自己的對話記錄。

聊天輸入框下方會顯示簡短提示。AI 的回覆**不構成醫療建議** —— 診斷及治療請諮詢獸醫。`,
        },
        {
          title: '9. 資料保留',
          content: `我們會在以下必要期間保留你的個人資料：

• 向你提供我們的服務
• 遵守法律義務
• 解決爭議
• 執行我們的協議

聊天對話記錄會於 90 日後自動刪除。你可隨時要求刪除你的帳戶。`,
        },
        {
          title: '10. 兒童私隱',
          content: `我們的服務不針對 13 歲以下兒童。我們不會故意收集 13 歲以下兒童的個人資料。如你認為我們收集了兒童的資料，請立即聯絡我們。`,
        },
        {
          title: '11. 政策變更',
          content: `我們可能會不時更新此私隱政策。重大變更將通過電郵或網站通知你。變更生效後你繼續使用我們的服務即表示接受更新後的政策。`,
        },
        {
          title: '12. 聯絡我們',
          content: `如對此私隱政策或我們的資料處理有疑問，請聯絡我們：

**電郵**：info@natuviapet.com
**Instagram**：@NatuviaPet

我們通常會在 24-48 小時內回覆。`,
        },
      ],
    },
    termsPage: {
      h1: '條款及細則',
      seo: {
        title: '條款及細則｜Natuvia',
        description: '閱讀使用 Natuvia 網站及購買產品的條款及細則。',
      },
      hero: {
        tagline: '條款及細則',
        paragraph: '使用本網站或購買產品前，請仔細閱讀以下條款。',
      },
      lastUpdated: '最後更新：2025 年 1 月',
      sections: [
        {
          title: '1. 條款接受',
          content: `使用 Natuvia 網站（natuvia.com）即表示你同意受這些條款及細則約束。如你不同意這些條款，請勿使用我們的網站或服務。

這些條款適用於我們網站的所有訪客、用戶和客戶。`,
        },
        {
          title: '2. 產品和服務',
          content: `**產品資訊**
我們致力提供準確的產品描述、圖片和定價。然而，我們不保證產品描述或其他內容完全準確、完整或無錯誤。

**供應情況**
所有產品視乎供應情況而定。我們保留限制數量和停止產品銷售的權利，恕不另行通知。

**定價**
價格以所選貨幣顯示，可能會有變動。任何推廣或折扣只在指定期間內有效。`,
        },
        {
          title: '3. 訂單和付款',
          content: `**訂單接受**
你的訂單構成購買要約。我們保留因任何原因接受或拒絕任何訂單的權利，包括產品供應、定價錯誤或涉嫌欺詐。

**付款**
我們接受主要信用卡和結帳時顯示的其他付款方式。付款通過 Stripe 安全處理。你同意提供準確的付款資料。

**訂單確認**
下單後你將收到電郵確認。此確認不保證你的訂單已被接受。`,
        },
        {
          title: '4. 運送和交付',
          content: `**運送地區**
我們目前運送至香港和部分國際地區。運送範圍和費用會在結帳時顯示。

**交付時間**
預計交付時間僅供參考，不作保證。Natuvia 不對運輸公司造成的延誤或超出我們控制範圍的情況負責。

**損失風險**
產品交付給運輸公司後，損失風險和所有權即轉移給你。`,
        },
        {
          title: '5. 退換貨和退款',
          content: `**退貨政策**
未開封產品可於交付後 14 天內退回，獲全額退款。產品必須保持原包裝、未開封狀態。

**損壞或有缺陷產品**
如收到損壞或有缺陷的產品，請在交付後 48 小時內連同照片聯絡我們。我們會安排更換或退款。

**退款處理**
退款將在我們收到退回產品後 5-10 個工作天內退回原付款方式。

**不可退貨項目**
已開封產品、易腐產品和超過使用期限的產品不可退貨。`,
        },
        {
          title: '6. 用戶帳戶',
          content: `**帳戶建立**
你可以建立帳戶以使用額外功能。你有責任保持帳戶憑證的機密性。

**帳戶準確性**
你同意在建立帳戶時提供準確完整的資料，並在需要時更新你的資料。

**帳戶終止**
我們保留暫停或終止違反這些條款的帳戶的權利，或因任何其他原因自行決定終止。`,
        },
        {
          title: '7. 知識產權',
          content: `本網站上的所有內容，包括文字、圖片、標誌、圖形和軟件，均為 Natuvia 或其授權人的財產，受知識產權法保護。

未經我們明確書面許可，你不得複製、分發、修改或創作任何內容的衍生作品。`,
        },
        {
          title: '8. 用戶內容',
          content: `**寵物檔案和評論**
當你提交寵物檔案或評論等內容時，你授予 Natuvia 非獨家、免版稅的許可使用、展示和分發該內容。

**內容標準**
你同意不提交以下內容：
• 非法、有害或冒犯性的內容
• 侵犯知識產權的內容
• 虛假、誤導或欺騙性的內容
• 垃圾郵件或未經請求的廣告`,
        },
        {
          title: '9. 免責聲明',
          content: `我們的網站和產品按「現狀」提供，不作任何明示或暗示的保證。

**營養資訊**
產品資訊，包括營養成分和餵食指南，僅供一般參考。如有特定飲食需求，請諮詢獸醫。

**寵物健康**
Natuvia 產品不用於診斷、治療、治癒或預防任何疾病。如有健康問題，請諮詢獸醫。`,
        },
        {
          title: '10. 責任限制',
          content: `在法律允許的最大範圍內，Natuvia 不對以下情況引起的任何間接、附帶、特殊、後果性或懲罰性損害負責：

• 使用或無法使用我們的網站或產品
• 通過我們網站購買的任何產品
• 未經授權存取你的資料
• 任何第三方內容或行為`,
        },
        {
          title: '11. 賠償',
          content: `你同意賠償並使 Natuvia 免受因你使用我們網站、違反這些條款或侵犯任何第三方權利而引起的任何索賠、損害、損失或費用。`,
        },
        {
          title: '12. 適用法律',
          content: `這些條款受香港特別行政區法律管轄並據此解釋。任何爭議須受香港法院專屬管轄。`,
        },
        {
          title: '13. 條款變更',
          content: `我們可能會不時更新這些條款。變更將在本頁面發布並更新「最後更新」日期。變更後你繼續使用我們的網站即表示接受修改後的條款。`,
        },
        {
          title: '14. 聯絡資料',
          content: `如對這些條款有疑問，請聯絡我們：

**電郵**：info@natuviapet.com
**Instagram**：@NatuviaPet

我們通常會在 24-48 小時內回覆。`,
        },
      ],
    },
    faqPage: {
      h1: '常見問題',
      seo: {
        title: '常見問題｜Natuvia',
        description: '查看關於 Natuvia 產品、運送等常見問題的解答。',
      },
      hero: {
        tagline: '常見問題',
        paragraph: '找不到答案？歡迎聯絡我們。',
      },
      categories: {
        products: '產品',
        shipping: '運送與訂單',
        feeding: '餵食指南',
        other: '其他',
      },
      items: [
        {
          category: 'products',
          q: 'Natuvia 與其他寵物食品品牌有什麼不同？',
          a: '我們專注於「清楚」：單一動物蛋白、極簡成分、清晰標示。每種成分都有其目的，我們會用簡單易懂的語言說明——沒有行銷術語。',
        },
        {
          category: 'products',
          q: '你們的產品適合有過敏的寵物嗎？',
          a: '我們的單一蛋白配方旨在更容易識別和避免致敏原。建議查看成分表，如寵物有已知過敏，請諮詢獸醫。',
        },
        {
          category: 'products',
          q: '產品應該如何儲存？',
          a: '存放於陰涼乾燥處，避免陽光直射。開封後請密封保存，並在包裝標示的期限內食用完畢。',
        },
        {
          category: 'feeding',
          q: '應該餵多少份量？',
          a: '餵食量因產品、寵物體型及活動量而異。請參考各產品頁面的餵食指南。',
        },
        {
          category: 'feeding',
          q: '可以混合其他食物餵食嗎？',
          a: '可以！我們的凍乾產品非常適合作為 topper 或零食，搭配日常主糧使用。請適量調整份量。',
        },
        {
          category: 'shipping',
          q: '配送需時多久？',
          a: '香港訂單通常 2-4 個工作天送達。海外配送時間因地區而異。',
        },
        {
          category: 'shipping',
          q: '有免運費嗎？',
          a: '有！香港訂單滿 HK$300 即享免運費。',
        },
        {
          category: 'shipping',
          q: '退貨政策是什麼？',
          a: '未開封產品可於 14 天內退貨。請發電郵至 info@natuviapet.com 申請。',
        },
        {
          category: 'other',
          q: '如何聯絡 Natuvia？',
          a: '可發電郵至 info@natuviapet.com 或透過 Instagram @NatuviaPet 聯絡我們。通常 24-48 小時內回覆。',
        },
        {
          category: 'other',
          q: '你們有實體店嗎？',
          a: '我們目前只在網上銷售，這讓我們能專注於產品質素並保持合理價格。',
        },
      ],
      cta: {
        title: '還有其他問題？',
        copy: '我們樂意幫忙。',
        button: '聯絡我們',
      },
    },
    contactPage: {
      h1: '聯絡我們',
      seo: {
        title: '聯絡我們｜Natuvia',
        description: '對 Natuvia 產品有任何問題？歡迎透過電郵或社交媒體與我們聯繫。',
      },
      hero: {
        tagline: '期待你的來信',
        paragraph: '無論是產品問題、餵食建議，還是想打個招呼——我們都很樂意幫忙。',
      },
      form: {
        title: '傳送訊息',
        name: '你的名字',
        namePlaceholder: '請輸入你的名字',
        email: '電郵地址',
        emailPlaceholder: 'your@email.com',
        subject: '主題',
        subjectPlaceholder: '想問什麼呢？',
        message: '訊息內容',
        messagePlaceholder: '請告訴我們如何幫助你...',
        submit: '傳送訊息',
        sending: '傳送中...',
        success: '訊息已送出！我們會盡快回覆你。',
        error: '發生錯誤，請稍後再試或直接發電郵給我們。',
      },
      info: {
        title: '其他聯絡方式',
        email: {
          label: '電郵',
          value: 'info@natuviapet.com',
        },
        social: {
          label: '追蹤我們',
        },
        response: {
          label: '回覆時間',
          value: '我們通常會在 24-48 小時內回覆。',
        },
      },
    },
    chat: {
      title: 'Natuvía 小幫手',
      placeholder: '輸入問題...',
      welcome: '你好！我是 AI 助理，有關於產品或營養的問題都可以問我',
      quickQuestions: {
        title: '常見問題',
        items: [
          '我該如何挑選適合我寵物的蛋白？',
          '如何閱讀成分表，避免被行銷字眼誤導？',
          '凍乾雞胸是否適合每日餵食？該如何餵？',
          '腸胃敏感／皮膚痕癢時，蛋白選擇有何方向？',
          '可作零食或 topper 嗎？如何與主糧搭配？',
          '請推薦最相關的 Journal 文章',
        ],
      },
    },
  },
};
